/* Colours, faces and radii come from the `--proof-*` tokens each product sets in its own index.scss. */

$narrow: 760px;

.page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--proof-bg);
  color: var(--proof-text);
  font-family: var(--proof-font-body);
}

/* The body drawn inside another page: the same ground, type and colour, no full-height frame. */
.inline {
  display: flex;
  flex-direction: column;
  background: var(--proof-bg);
  color: var(--proof-text);
  font-family: var(--proof-font-body);
}

.wrap {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 16px;
  box-sizing: border-box;
}

.disp1,
.disp2,
.disp3 {
  margin: 0;
  font-family: var(--proof-font-display);
  font-style: var(--proof-display-style);
  font-weight: var(--proof-display-weight);
  line-height: 1.1;
  color: var(--proof-text);
}

.disp1 { font-size: 44px; }
.disp2 { font-size: 40px; }
.disp3 { font-size: 28px; }

@media (max-width: $narrow) {
  .disp1 { font-size: 34px; }
  .disp2 { font-size: 30px; }
  .disp3 { font-size: 24px; }
}

.body {
  margin: 0;
  font-size: 17px;
  line-height: 1.55;
  color: var(--proof-muted);
}

/* Hero and contact sheet */

.hero {
  padding-top: 48px;
  padding-bottom: 64px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.heroText {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  max-width: 880px;
}

.sheet {
  display: grid;
  grid-template-columns: repeat(9, minmax(0, 1fr));
  gap: 6px;

  @media (max-width: $narrow) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.ph {
  margin: 0;
  border-radius: var(--proof-radius-sm);
  overflow: hidden;
  background: var(--proof-surface);
  aspect-ratio: 2 / 3;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

/* Demos */

.demos {
  background: var(--proof-band);
  border-top: 1px solid var(--proof-border);

  .disp2 { margin-bottom: 8px; }
  > .wrap > .body { margin-bottom: 16px; }
}

.demosEnd { border-bottom: 1px solid var(--proof-border); }

.demosHead {
  padding-top: 72px;
  padding-bottom: 32px;
}

.demo {
  display: flex;
  flex-wrap: wrap;
  gap: 48px;
  align-items: center;
  padding: 56px 0;
  border-top: 1px solid var(--proof-border);

  @media (max-width: $narrow) {
    gap: 24px;
    padding: 40px 0;
  }
}

.dtext {
  flex: 1 1 320px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
}

.dvis {
  flex: 999 1 560px;
  min-width: 0;
}

.num {
  font-family: var(--proof-font-mono);
  font-size: 12px;
  color: var(--proof-accent);
}

.mosaic {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 6px;
}

.searchStack {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.field {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--proof-surface);
  border: 1px solid var(--proof-border-strong);
  border-radius: var(--proof-radius-lg);
  padding: 14px 18px;
  font-size: 18px;
  color: var(--proof-text);
  text-decoration: none;

  svg { color: var(--proof-muted); flex: none; }

  &:hover { border-color: var(--proof-muted); }
}

.pair {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
}

.pairItem {
  flex: 1 1 200px;
  max-width: 280px;
}

.pairEdit .pairItem { max-width: 240px; }

/* On a phone an edit is one screen: the words over the pair, before and after side by side. */
@media (max-width: $narrow) {
  .pairEdit {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 6px;

    .sentence { grid-column: 1 / -1; order: -1; }
    .pairItem { max-width: none; }
  }
}

/* A face pair on a phone: the catalogue picture and hers side by side, the arrow dropped. */
@media (max-width: $narrow) {
  .pairFace {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 6px;

    .arrow { display: none; }
    .pairItem { max-width: none; }
  }
}

.arrow { color: var(--proof-accent); flex: none; }

.tag {
  display: block;
  margin-top: 8px;
  font-family: var(--proof-font-mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--proof-dim);
}

.sentence {
  flex: 1 1 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: var(--proof-radius-lg);
  background: var(--proof-pill-bg);
  border: 1px solid var(--proof-pill-border);
  color: var(--proof-text);
  font-size: 16px;

  svg { color: var(--proof-pill-ink); flex: none; }
}

.trio {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}

/* Chips */

.chipsBlock {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-top: 56px;
  padding-bottom: 56px;
}

/* Pack cards: a 2:3 picture with its label under it, the whole card a link. Whole rows at each width. */

.cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px 8px;

  @media (max-width: $narrow) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  text-decoration: none;

  .ph { transition: box-shadow 0.15s; }
  .themeName { transition: color 0.15s; }

  &:hover .ph,
  &:focus-visible .ph { box-shadow: 0 0 0 2px var(--proof-accent); }

  &:hover .themeName,
  &:focus-visible .themeName { color: var(--proof-accent); }

  &:focus-visible { outline: none; }
}

/* A picture that opens a page */

.phLink {
  display: block;
  width: 100%;
  height: 100%;
  outline: none;
}

.ph:has(> .phLink:hover),
.ph:has(> .phLink:focus-visible) { box-shadow: 0 0 0 2px var(--proof-accent); }

/* A grid of whole rows: `--cols` pictures a row, the page sends cols × rows and the phone's rows show the first
   `--cols-narrow` × rowsNarrow; the rest are `display: none`, so their lazy images are never fetched. */

.rows {
  display: grid;
  grid-template-columns: repeat(var(--cols), minmax(0, 1fr));
  gap: 6px;

  @media (max-width: $narrow) {
    grid-template-columns: repeat(var(--cols-narrow), minmax(0, 1fr));

    .beyondNarrow { display: none; }
  }
}

/* Closing */

.closing {
  padding-top: 80px;
  padding-bottom: 96px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;
  text-align: center;
}

.verb {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  margin-top: 8px;
  padding: 12px 24px;
  border-radius: var(--proof-radius-md);
  background: var(--proof-accent);
  color: var(--proof-accent-ink);
  font-size: 16px;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  border: 0;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: var(--proof-accent-hover); color: var(--proof-accent-ink); }
}

/* Mixed-shape sheet: columns of pictures at their own proportions */

.sheetMixed {
  display: block;
  column-count: 6;
  column-gap: 6px;

  @media (max-width: $narrow) {
    column-count: 3;
  }

  .ph {
    break-inside: avoid;
    margin-bottom: 6px;
  }
}

.edits {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* A ready picture beside the empty box it replaces */

/* A product's own interface, drawn by the product and shown still: the page decides only where it stands. */

.bar {
  pointer-events: none;
  user-select: none;
}

/* How a muse is made: three stages, one under the other */

.steps {
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 560px;

  .arrow {
    align-self: center;
    transform: rotate(90deg);
  }
}

.step { min-width: 0; }

.six {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 6px;
}

/* Themes: a row of pictures, each with its name */

.themes {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px 8px;

  @media (max-width: $narrow) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.theme {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.themeName {
  font-size: 15px;
  color: var(--proof-text);
}

.theme .tag { margin-top: 0; }

/* "It never runs out": one grid, the lead picture spanning two columns and two rows, its neighbours filling whole rows
   around it, the last row faded. The page sends the whole wall; a picture past the visible rows is `display: none`, so
   its lazy image is never fetched. Visible pictures = cols × rows − 3 (the lead takes four cells). */
$neighbour-cols: 5;
$neighbour-rows: 3;
$neighbour-cols-narrow: 3;
$neighbour-rows-narrow: 3;

@function neighbour-shown($cols, $rows) { @return $cols * $rows - 3; }

.neighbours {
  display: grid;
  grid-template-columns: repeat($neighbour-cols, minmax(0, 1fr));
  gap: 6px;
  -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - 96px), transparent);
  mask-image: linear-gradient(to bottom, #000 calc(100% - 96px), transparent);

  > :first-child {
    grid-column: span 2;
    grid-row: span 2;
    aspect-ratio: auto;
  }

  > :nth-child(n + #{neighbour-shown($neighbour-cols, $neighbour-rows) + 1}) { display: none; }

  @media (max-width: $narrow) {
    grid-template-columns: repeat($neighbour-cols-narrow, minmax(0, 1fr));

    > :nth-child(n + #{neighbour-shown($neighbour-cols, $neighbour-rows) + 1}) { display: block; }
    > :nth-child(n + #{neighbour-shown($neighbour-cols-narrow, $neighbour-rows-narrow) + 1}) { display: none; }
  }
}

.museRow { max-width: 560px; }

/* Lines and links the product has no picture for */

.plainList {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;

  li { display: flex; flex-direction: column; gap: 4px; }
}

.linkGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.linkCard {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px 18px;
  border: 1px solid var(--proof-border);
  border-radius: var(--proof-radius-lg);
  background: var(--proof-surface);
  text-decoration: none;

  &:hover { border-color: var(--proof-border-strong); }
  .body { font-size: 15px; }
}

.heroText .verb { align-self: flex-start; margin-top: 12px; }
